import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ResumeUpload from '../../components/common/resume/ResumeUpload';
import ResumesContent from '../../components/common/table/ResumesTable';
import { resumeApi } from '../../services/resumeApi';
import { offerLetterApi } from '../../services/offerLetter.service';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, Award, FileText, Brain, MessageSquare, Gift, TrendingUp, Zap } from 'lucide-react';
import OfferLetterForm from '../../components/common/offer/OfferLetterForm';
import OfferLetterModal from '../../components/common/offer/OfferLetterModal';
import api from '../../services/api';

// ── Selection Process Report ──────────────────────────────────────────────────
function StageRow({ icon: Icon, label, status, score, detail, color }) {
  const statusStyle = {
    shortlisted: 'bg-emerald-100 text-emerald-700',
    rejected:    'bg-red-100 text-red-600',
    completed:   'bg-blue-100 text-blue-700',
    not_started: 'bg-gray-100 text-gray-400',
    sent:        'bg-purple-100 text-purple-700',
    accepted:    'bg-emerald-100 text-emerald-700',
    generated:   'bg-blue-100 text-blue-700',
  };
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-50 last:border-0">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        {detail && <p className="text-xs text-gray-400 mt-0.5 truncate">{detail}</p>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {score > 0 && (
          <span className="text-sm font-bold text-orange-500">{Math.round(score)}%</span>
        )}
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusStyle[status] || 'bg-gray-100 text-gray-400'}`}>
          {status?.replace(/_/g, ' ') || 'Not Started'}
        </span>
      </div>
    </div>
  );
}

// STAGE_COLORS: one per pipeline stage type
const STAGE_COLORS = {
  pre_screening:    'bg-violet-500',
  psychoanalytical: 'bg-pink-500',
  final_interview:  'bg-orange-500',
  ai_interview:     'bg-indigo-500',
  technical:        'bg-blue-500',
  aptitude:         'bg-cyan-500',
  communication:    'bg-teal-500',
  personality:      'bg-fuchsia-500',
  coding:           'bg-emerald-600',
  case_study:       'bg-amber-500',
  gen_ai_readiness: 'bg-sky-500',
  custom:           'bg-slate-500',
};

// Map pipeline stage → resume score/status fields (for legacy rounds)
const ROUND_FIELD = {
  preScreening:     { score: 'preScreeningScore',    status: 'preScreeningStatus',    feedback: r => r.preScreeningResult?.feedback },
  psychoanalytical: { score: 'psychoanalyticalScore', status: 'psychoanalyticalStatus', feedback: r => r.psychoanalyticalResult?.feedback },
  finalInterview:   { score: 'finalInterviewScore',  status: 'finalInterviewStatus',  feedback: r => r.finalInterviewResult?.feedback },
  coding:           { score: 'codingScore',           status: 'codingStatus',           feedback: () => null },
};

function AICostSection({ resumeId }) {
  const [costs, setCosts]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);

  useEffect(() => {
    if (!resumeId || loaded) return;
    setLoading(true);
    api.get(`/reports/candidate/${resumeId}/costs`)
      .then(r => { setCosts(r.data?.data || null); setLoaded(true); })
      .catch(() => { setCosts(null); setLoaded(true); })
      .finally(() => setLoading(false));
  }, [resumeId, loaded]);

  if (loading) return (
    <div className="mt-4 p-4 bg-indigo-50 rounded-xl flex items-center gap-2 text-sm text-indigo-500">
      <Clock className="w-3.5 h-3.5 animate-spin" /> Loading AI cost data…
    </div>
  );
  if (!costs || (costs.totalUsd === 0 && costs.totalTokens === 0)) return null;

  return (
    <div className="mt-4">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <Zap className="w-3.5 h-3.5 text-indigo-500" /> Total AI Cost Incurred
      </p>
      {/* Grand total row */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-indigo-400 mb-0.5">Total Cost</div>
          <div className="text-lg font-black text-indigo-700">${costs.totalUsd.toFixed(4)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Total Tokens</div>
          <div className="text-lg font-black text-slate-700">{costs.totalTokens.toLocaleString()}</div>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">AI Operations</div>
          <div className="text-lg font-black text-slate-700">{costs.timeline?.length || 0}</div>
        </div>
      </div>
      {/* Per-stage breakdown */}
      {costs.byStage?.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-right px-3 py-2">Ops</th>
                <th className="text-right px-3 py-2">Tokens</th>
                <th className="text-right px-3 py-2">Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {costs.byStage.map((s, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{s.stage}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{s.count}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{s.tokens.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-semibold text-indigo-600">${s.usdCost.toFixed(4)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-200 bg-indigo-50">
                <td className="px-3 py-2 font-bold text-indigo-700">Total</td>
                <td className="px-3 py-2 text-right font-bold text-indigo-700">{costs.timeline?.length || 0}</td>
                <td className="px-3 py-2 text-right font-bold text-indigo-700">{costs.totalTokens.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-bold text-indigo-700">${costs.totalUsd.toFixed(4)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CandidateReport({ resume, pipelineStages }) {
  const [open, setOpen] = useState(false);

  // Build the stages to show: use configured pipeline, fall back to nothing
  const stagesToShow = pipelineStages || [];

  // Collect scores for average
  const allScores = [
    resume.aiScore,
    ...stagesToShow.map(s => {
      const psr = (resume.pipelineStageResults || []).find(p => p.stageId === s.stageId);
      if (psr) return psr.score;
      const rf = ROUND_FIELD[s.round];
      return rf ? resume[rf.score] : null;
    }),
  ].filter(s => s > 0);
  const avgScore = allScores.length ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : null;

  // Highest stage reached
  const lastPassed = [...stagesToShow].reverse().find(s => {
    const psr = (resume.pipelineStageResults || []).find(p => p.stageId === s.stageId);
    if (psr) return psr.status === 'shortlisted';
    const rf = ROUND_FIELD[s.round];
    return rf && resume[rf.status] === 'shortlisted';
  });
  const highestStage = resume.offerLetterStatus !== 'not_generated' ? 'Offer'
    : lastPassed?.label || (resume.resumeScreeningStatus === 'shortlisted' ? 'Resume Screening' : 'Applied');

  const outcome = resume.offerLetterStatus === 'accepted' ? '✓ Hired'
    : resume.offerLetterStatus === 'rejected' ? '✗ Offer Declined'
    : lastPassed ? 'In Progress'
    : '✗ Not Selected';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center font-bold text-orange-600 shrink-0">
            {(resume.candidateName || 'C')[0].toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-gray-800">{resume.candidateName || 'Unknown'}</p>
            <p className="text-xs text-gray-400">{resume.email}</p>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">{highestStage}</span>
          {avgScore && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-600 flex items-center gap-1">
              <Award className="w-3 h-3" /> Avg {avgScore}%
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mt-4 mb-3">Selection Journey</p>

          {/* Always show resume screening first */}
          <StageRow icon={FileText} label="Resume Screening"
            status={resume.resumeScreeningStatus} score={resume.aiScore}
            detail={resume.fileName} color="bg-indigo-500" />

          {/* Show only the configured pipeline stages */}
          {stagesToShow.length === 0 && (
            <p className="text-sm text-gray-400 py-3 text-center italic">No assessment pipeline configured for this JD.</p>
          )}
          {stagesToShow.map((stage, i) => {
            // Try pipelineStageResults first (custom stage with own score)
            const psr = (resume.pipelineStageResults || []).find(p => p.stageId === stage.stageId);
            if (psr) {
              return <StageRow key={i} icon={Award} label={stage.label}
                status={psr.status} score={psr.score}
                detail={`Pass score: ${stage.passingScore}%`}
                color={STAGE_COLORS[stage.type] || 'bg-slate-500'} />;
            }
            // Fall back to round fields for legacy-mapped stages
            const rf = ROUND_FIELD[stage.round];
            if (rf) {
              return <StageRow key={i} icon={MessageSquare} label={stage.label}
                status={resume[rf.status]} score={resume[rf.score]}
                detail={rf.feedback(resume) || `Pass score: ${stage.passingScore}%`}
                color={STAGE_COLORS[stage.type] || STAGE_COLORS[stage.round] || 'bg-slate-500'} />;
            }
            return null;
          })}

          {/* Offer letter */}
          <StageRow icon={Gift} label="Offer Letter"
            status={resume.offerLetterStatus} score={null}
            detail={resume.offerLetterStatus !== 'not_generated' ? `Offer ${resume.offerLetterStatus}` : null}
            color="bg-emerald-500" />

          {/* Summary */}
          <div className="mt-4 p-4 bg-gray-50 rounded-xl">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Summary</p>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="text-gray-600">Assessments: <strong className="text-gray-800">{stagesToShow.length}</strong></span>
              {avgScore && <span className="text-gray-600">Avg score: <strong className="text-orange-500">{avgScore}%</strong></span>}
              <span className="text-gray-600">Reached: <strong className="text-indigo-600">{highestStage}</strong></span>
              <span className={`font-semibold ${outcome.startsWith('✓') ? 'text-emerald-600' : outcome.startsWith('✗') ? 'text-red-500' : 'text-gray-600'}`}>
                {outcome}
              </span>
            </div>
          </div>

          {/* AI Cost — lazy-loaded when the card expands */}
          <AICostSection resumeId={resume._id} />
        </div>
      )}
    </div>
  );
}

function SelectionReports({ resumes, pipelineStages }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-bold text-gray-800">Candidate Selection Reports</h2>
        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-semibold">{resumes.length}</span>
        {pipelineStages.length > 0 && (
          <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
            {pipelineStages.length} assessment stage{pipelineStages.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {resumes.map(r => <CandidateReport key={r._id} resume={r} pipelineStages={pipelineStages} />)}
      </div>
    </div>
  );
}

const OfferLetterPage = () => {
  const { id: jobId } = useParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingOffer, setGeneratingOffer] = useState(null);
  // New form + viewer state
  const [showFormFor, setShowFormFor]   = useState(null); // { resumeId, candidateName }
  const [viewOffer, setViewOffer]       = useState(null); // { content, company, resumeId }
  const [checkingTemplate, setCheckingTemplate] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalResumes, setTotalResumes] = useState(0);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  const [filteredResumes, setFilteredResumes] = useState([]);
  
  // Filter states for each stage — default 'all' so all candidates are visible
  // (candidates on custom pipelines don't have legacy preScreeningStatus etc. set,
  //  so defaulting to 'shortlisted' would silently hide everyone)
  const [preScreeningFilter, setPreScreeningFilter] = useState('all');
  const [psychoanalyticalFilter, setPsychoanalyticalFilter] = useState('all');
  const [finalInterviewFilter, setFinalInterviewFilter] = useState('all');

  // Fetch job details and resumes
  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  useEffect(() => {
    if (!checkingTemplate && hasTemplate) {
      fetchShortlistedResumes();
    }
  }, [jobId, currentPage, itemsPerPage, checkingTemplate, hasTemplate]);

  // ── Real-time score polling ──────────────────────────────────────────
  // Poll every 30 s while any candidate still has an interview in the "sent"
  // state (any round) so scores update without manual refresh.
  useEffect(() => {
    const hasPending = resumes.some(r =>
      r.inviteStatus?.preScreening     === 'sent' ||
      r.inviteStatus?.psychoanalytical === 'sent' ||
      r.inviteStatus?.finalInterview   === 'sent' ||
      r.inviteStatus?.coding           === 'sent'
    );
    if (!hasPending) return;
    const timer = setInterval(() => { fetchShortlistedResumes(); }, 30000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumes]);

  // No client-side stage filtering needed — server already returns only
  // candidates shortlisted in the last pipeline stage via 'last_stage_shortlisted'.
  const applyFilters = () => {
    setFilteredResumes([...resumes]);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchShortlistedResumes();
  };

  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
    fetchShortlistedResumes();
  };

  const fetchJobDetails = async () => {
    try {
      setCheckingTemplate(true);
      const [jobRes, pipelineRes] = await Promise.all([
        offerLetterApi.getJobDetails(jobId),
        api.get(`/jobs/${jobId}/effective-pipeline`).catch(() => null),
      ]);
      const template = jobRes.data.offerLetterTemplate;
      const templateExists = !!template;
      setHasTemplate(templateExists);
      setTemplateInfo(template || null);
      setShowUploadModal(!templateExists);
      // Store ALL pipeline stages (custom + legacy) so CandidateReport can
      // show the full journey and so we know which is the final qualifying stage.
      const stages = pipelineRes?.data?.data?.stages || [];
      setPipelineStages(stages);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to check offer letter template');
    } finally {
      setCheckingTemplate(false);
    }
  };

  const fetchShortlistedResumes = async () => {
    try {
      setLoading(true);
      // Always fetch candidates shortlisted in the last pipeline stage —
      // works for both legacy and custom pipelines.
      const response = await resumeApi.getAllResumes(
        jobId,
        currentPage,
        itemsPerPage,
        'last_stage_shortlisted'
      );
      
      const fetched = response.data.resumes || [];
      console.log(`[offer-letter] fetched ${fetched.length} last-stage-shortlisted candidates`);
      setResumes(fetched);
      setFilteredResumes(fetched);
      setTotalResumes(response.data.total || fetched.length);
      setStatusCounts(response.data.statusCounts || {});
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateUpload = async (files) => {
    try {
      setLoading(true);
      const file = files[0];
      const formData = new FormData();
      formData.append('template', file);
      formData.append('jobId', jobId);

      await offerLetterApi.uploadTemplate(jobId, formData);
      setHasTemplate(true);
      setShowUploadModal(false);
      toast.success('Template uploaded — offer letters will now use this template');
      // Re-fetch job details so the template info is fresh for generation
      await fetchJobDetails();
    } catch (error) {
      console.error('Error uploading template:', error);
      toast.error('Failed to upload template');
    } finally {
      setLoading(false);
    }
  };

  // Open the form instead of auto-generating
  const onGenerateOffer = (resumeId) => {
    const resume = resumes.find((r) => r._id === resumeId);
    setShowFormFor({ resumeId, candidateName: resume?.candidateName || "Candidate" });
  };

  // Called when the form successfully generates
  const onOfferGenerated = (result) => {
    fetchShortlistedResumes();
    if (result?.offerLetter) {
      setViewOffer({ content: result.offerLetter, company: result.company || null, resumeId: showFormFor?.resumeId || null });
    }
  };

  const onSendOffer = async (resumeId) => {
    try {
      setLoading(true);
      
      // Use the offer letter API to send the offer
      const response = await offerLetterApi.sendOffer(resumeId);
      
      if (response.success) {
        toast.success('Offer letter sent successfully');
        fetchShortlistedResumes();
      } else {
        throw new Error(response.message || 'Failed to send offer letter');
      }
    } catch (error) {
      console.error('Error sending offer:', error);
      toast.error('Failed to send offer letter');
    } finally {
      setLoading(false);
    }
  };

  // Filter dropdown component
  const FilterDropdown = ({ label, value, onChange }) => (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="rejected">Rejected</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <ChevronDown className="h-4 w-4" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Offer Letters</h1>
        {!checkingTemplate && (
          <div className="flex items-center gap-3">
            {hasTemplate && templateInfo && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Template: {templateInfo.fileName || 'Uploaded'}
              </div>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 text-sm font-semibold transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              {hasTemplate ? 'Update Template' : 'Upload Template'}
            </button>
          </div>
        )}
      </div>

      {checkingTemplate ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600">Checking offer letter template...</p>
        </div>
      ) : (
        <>
          {showUploadModal && (
            <ResumeUpload
              isOpen={showUploadModal}
              onClose={() => setShowUploadModal(false)}
              onUpload={handleTemplateUpload}
              title="offerLetter"
              accept=".doc,.docx,.pdf"
              maxFiles={1}
            />
          )}

          {hasTemplate && pipelineStages.length > 0 && (() => {
            const enabledStages = pipelineStages.filter(s => s.enabled !== false);
            const lastStage = enabledStages[enabledStages.length - 1];
            return lastStage ? (
              <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
                <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Showing candidates <strong className="mx-1">shortlisted</strong> in the final stage:
                <span className="ml-1 px-2 py-0.5 bg-emerald-100 rounded-full font-semibold text-emerald-700">{lastStage.label}</span>
                <span className="ml-auto text-xs text-emerald-500">({filteredResumes.length} candidate{filteredResumes.length !== 1 ? 's' : ''})</span>
              </div>
            ) : null;
          })()}

          {hasTemplate && (
            <div className="mt-6">
              {loading ? (
                <div className="flex justify-center items-center min-h-[200px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
                </div>
              ) : (
                <ResumesContent
                  resumes={filteredResumes}
                  pagination={{
                    page: currentPage,
                    totalPages: Math.ceil(totalResumes / itemsPerPage),
                    total: totalResumes,
                    limit: itemsPerPage,
                    statusCounts: statusCounts
                  }}
                  setPagination={(newPagination) => {
                    setCurrentPage(newPagination.page);
                    setItemsPerPage(newPagination.limit);
                  }}
                  onPageChange={handlePageChange}
                  currentFilter={currentFilter}
                  onFilterChange={handleFilterChange}
                  screenType="offer-letter"
                  pipelineStages={pipelineStages}
                  onGenerateOffer={onGenerateOffer}
                  onSendOffer={onSendOffer}
                  onStatusChange={async (resumeId, type, newStatus, override) => {
                    try {
                      await resumeApi.updateResumeStatus(resumeId, type, newStatus, override);
                      fetchShortlistedResumes();
                    } catch { /* toast shown by dropdown */ }
                  }}
                  loading={loading}
                  generatingOffer={generatingOffer}
                />
              )}

              {/* Selection Process Reports */}
              {!loading && filteredResumes.length > 0 && (
                <SelectionReports resumes={filteredResumes} pipelineStages={pipelineStages} />
              )}
            </div>
          )}
        </>
      )}
      {/* Offer form — HR enters CTC + position before generating */}
      {showFormFor && (
        <OfferLetterForm
          isOpen={!!showFormFor}
          onClose={() => setShowFormFor(null)}
          resumeId={showFormFor.resumeId}
          candidateName={showFormFor.candidateName}
          onGenerated={onOfferGenerated}
        />
      )}

      {/* Offer viewer — markdown + logo + printable PDF */}
      {viewOffer && (
        <OfferLetterModal
          isOpen={!!viewOffer}
          onClose={() => setViewOffer(null)}
          content={viewOffer.content}
          company={viewOffer.company}
          resumeId={viewOffer.resumeId}
        />
      )}
    </div>
  );
};

export default OfferLetterPage;